import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { API_BASE_URL, authFetch } from "../../config/api";

const emptyForm = {
  name: "",
  status: "active",
};

const Particulars = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [disableTarget, setDisableTarget] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDisable, setShowBulkDisable] = useState(false);

  useEffect(() => {
    fetchParticulars();
  }, []);

  const fetchParticulars = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/particulars`, {
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) setList(data.data || []);
    } catch (err) {
      console.error("fetch particulars", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const method = editingId ? "PUT" : "POST";
    const url = editingId
      ? `${API_BASE_URL}/api/accounting/particulars/${editingId}`
      : `${API_BASE_URL}/api/accounting/particulars`;
    try {
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setForm(emptyForm);
        setEditingId(null);
        fetchParticulars();
        setShowModal(false);
      } else {
        alert(data.message || "Save failed");
      }
    } catch (err) {
      console.error("save particular", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      status: row.status || "active",
    });
    setShowModal(true);
  };

  const handleDeactivateRequest = (row) => setDisableTarget(row);

  const confirmDeactivate = async () => {
    if (!disableTarget?.id) return;
    await authFetch(`${API_BASE_URL}/api/accounting/particulars/${disableTarget.id}`, {
      method: "DELETE",
      credentials: "include",
    });
    setDisableTarget(null);
    fetchParticulars();
  };

  const handleFilePick = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) {
        alert("No rows found in CSV.");
        return;
      }
      setPreviewRows(rows);
      setShowImportModal(true);
    } catch (err) {
      console.error("read csv", err);
      alert("Could not read CSV file.");
    }
  };

  const confirmImport = async () => {
    if (!previewRows.length) return;
    setImporting(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/particulars/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: previewRows }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || "Import failed");
      } else {
        setShowImportModal(false);
        setPreviewRows([]);
        fetchParticulars();
      }
    } catch (err) {
      console.error("import", err);
      alert("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const filtered = list.filter((row) => {
    const term = search.toLowerCase();
    if (!term) return true;
    return row.name?.toLowerCase().includes(term);
  });

  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((r) => r.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="space-y-6 font-poppins">
      <div className="bg-white/95 backdrop-blur-sm border border-gray-100 rounded-3xl shadow-sm p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Accounting</p>
          <h1 className="text-2xl font-semibold text-blue">Particulars</h1>
          <p className="text-sm text-gray-600">
            Simple name list used in requests/vouchers. Import supports a single column: name.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setEditingId(null);
              setForm(emptyForm);
              setShowModal(true);
            }}
            className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue/90 shadow-sm"
          >
            + Add Particular
          </button>
          <label className="cursor-pointer text-sm font-semibold text-blue underline">
            Import CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handleFilePick(e.target.files?.[0])}
              disabled={importing}
            />
          </label>
          <button
            type="button"
            onClick={fetchParticulars}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-gray-300"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white/95 rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-blue">Particulars List</h3>
            <p className="text-xs text-gray-500">Total: {filtered.length}</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name"
              className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue focus:ring-1 focus:ring-blue outline-none"
            />
            {loading && <span className="text-sm text-gray-500">Loading…</span>}
            <button
              type="button"
              disabled={!selectedIds.length}
              onClick={() => setShowBulkDisable(true)}
              className="px-3 py-2 rounded-lg border border-red/30 text-red text-xs font-semibold hover:bg-red/10 disabled:opacity-50"
            >
              Delete Selected ({selectedIds.length})
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                <th className="px-5 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === filtered.length && filtered.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4"
                  />
                </th>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50/80">
                  <td className="px-5 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelect(row.id)}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="px-5 py-3 font-medium text-gray-800">{row.name}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        row.status === "active"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right space-x-2">
                    <button
                      onClick={() => handleEdit(row)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-blue/30 text-blue text-xs font-semibold hover:bg-blue/10"
                    >
                      <i className="fa-solid fa-pen"></i> Edit
                    </button>
                    <button
                      onClick={() => handleDeactivateRequest(row)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red/30 text-red text-xs font-semibold hover:bg-red/10"
                    >
                      <i className="fa-solid fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td className="px-5 py-5 text-center text-gray-500" colSpan={3}>
                    No particulars found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal &&
        ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
            <h2 className="text-xl font-semibold text-blue mb-4">
              {editingId ? "Update Particular" : "Add Particular"}
            </h2>
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div className="flex flex-col md:col-span-2">
                <label className="text-xs text-gray-500 mb-1">Name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 focus:border-blue focus:ring-1 focus:ring-blue outline-none text-sm"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>
              <div className="md:col-span-2 flex gap-3 mt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue text-white rounded-lg text-sm font-semibold hover:bg-blue/90 shadow-sm disabled:opacity-60"
                >
                  {saving ? "Saving..." : editingId ? "Update Particular" : "Create Particular"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setForm(emptyForm);
                    setShowModal(false);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {disableTarget &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
              <button
                onClick={() => setDisableTarget(null)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-blue mb-2">Disable Particular</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold text-gray-800">{disableTarget.name}</span>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={confirmDeactivate}
                  className="px-4 py-2 bg-red text-white rounded-lg text-sm font-semibold hover:bg-red/90 shadow-sm"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDisableTarget(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showBulkDisable &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
              <button
                onClick={() => setShowBulkDisable(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-blue mb-2">Delete Selected Particulars</h3>
              <p className="text-sm text-gray-600 mb-4">
                Delete {selectedIds.length} selected particulars?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    setShowBulkDisable(false);
                    setImporting(true);
                    try {
                      await Promise.all(
                        selectedIds.map((id) =>
                          authFetch(`${API_BASE_URL}/api/accounting/particulars/${id}`, {
                            method: "DELETE",
                          })
                        )
                      );
                      setSelectedIds([]);
                      fetchParticulars();
                    } finally {
                      setImporting(false);
                    }
                  }}
                  className="px-4 py-2 bg-red text-white rounded-lg text-sm font-semibold hover:bg-red/90 shadow-sm"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowBulkDisable(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showImportModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 relative">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setPreviewRows([]);
                }}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
              <h3 className="text-lg font-semibold text-blue mb-3">Import Preview ({previewRows.length} rows)</h3>
              <div className="border border-gray-100 rounded-xl overflow-hidden mb-4 max-h-80 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                      <th className="px-4 py-2">name</th>
                      <th className="px-4 py-2">status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewRows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/80">
                        <td className="px-4 py-2">{row.name}</td>
                        <td className="px-4 py-2">{row.status || "active"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={confirmImport}
                  disabled={importing}
                  className="px-4 py-2 bg-blue text-white rounded-lg text-sm font-semibold hover:bg-blue/90 shadow-sm disabled:opacity-60"
                >
                  {importing ? "Importing..." : "Confirm Import"}
                </button>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setPreviewRows([]);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cols = line.split(",").map((c) => c.trim());
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = cols[idx] || "";
    });
    return obj;
  });
}

export default Particulars;
